/**
 * Recurring task schedules — queries and mutations over the v1 client.
 *
 * Schedules store a rule, never a materialised list of future runs, so
 * the occurrence query is a WINDOW query: the server expands the rules
 * for exactly the range the calendar is showing. That is why the window
 * is part of the cache key, and why every mutation invalidates the whole
 * `schedule-occurrences` prefix rather than one entry — a rule edit
 * changes what every already-cached window expands to, including the
 * ones the user paged past a minute ago.
 *
 * Query keys are site-scoped, matching `src/api/scope.ts`. They are
 * declared here rather than there because schedules landed after that
 * file was frozen for this change; they should move into `scope.ts` with
 * the rest when it is next touched.
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { usePreviewRole } from '../../auth/useRolePreview';
import { isFireModeHold } from './useFireMode';
import { invalidatePlanCheck } from './usePlanCheck';
import { tasksQueryOptions } from '../query-options';
import {
  ApiClientError,
  addScheduleExdate,
  createSchedule,
  deleteSchedule,
  fetchScheduleOccurrences,
  fetchSchedules,
  previewScheduleUpdate,
  putScheduleOverride,
  runScheduleOccurrence,
  updateSchedule,
  type RunOccurrenceResponse,
} from '../v1-client';
import type {
  CreateTaskScheduleRequest,
  ScheduleOccurrence,
  ScheduleOccurrenceOverride,
  ScheduleOccurrencePage,
  ScheduleUpdatePreview,
  SiteId,
  TaskSchedule,
  UpdateTaskScheduleRequest,
} from '../v1';

export const SCHEDULES_KEY = 'schedules' as const;
export const SCHEDULE_OCCURRENCES_KEY = 'schedule-occurrences' as const;
export const SCHEDULE_PREVIEW_KEY = 'schedule-preview' as const;

export const schedulesKey = (siteId: string) => [SCHEDULES_KEY, siteId] as const;
export const scheduleOccurrencesKey = (siteId: string, fromMs: number, toMs: number) =>
  [SCHEDULE_OCCURRENCES_KEY, siteId, fromMs, toMs] as const;
/** The body is part of the key: react-query hashes it with sorted keys,
 *  so the same edit asked twice is one cache entry however it was built. */
export const schedulePreviewKey = (
  siteId: string,
  scheduleId: string,
  body: UpdateTaskScheduleRequest,
) => [SCHEDULE_PREVIEW_KEY, siteId, scheduleId, body] as const;

/** The half-open window a calendar view is currently showing. */
export interface OccurrenceWindow {
  fromMs: number;
  toMs: number;
}

/**
 * A refused command carrying `fire_mode_active` is a HOLD, not a denial:
 * the caller has the capability and the request was well formed, but the
 * site is frozen behind an open fire event. Callers must say so in those
 * words — rendering it with the generic error copy reads as "you are not
 * allowed to do this", which sends the operator to look for a permission
 * problem that does not exist.
 */
export function isFireModeError(err: unknown): boolean {
  // Delegates rather than comparing `err.code` itself. The api-server
  // raises `HTTPException(detail=<ApiError>)`, so Starlette puts the
  // real code at `{detail: {code}}`; `ApiClientError`'s constructor
  // reads `code` off the TOP level, finds none, and stamps every
  // backend failure `code: 'internal'`. This predicate therefore
  // returned false for the one case it exists to catch, and its two
  // callers (`TasksScreen`, `TaskDispatchForm`) rendered the generic
  // message instead of the hold copy. `isFireModeHold` digs through
  // all three envelopes the SPA can receive.
  return isFireModeHold(err);
}

export const FIRE_MODE_HOLD_COPY =
  'Held by fire mode. This site is frozen behind an open fire event — nothing was sent. Dispatch resumes when the alarm is resolved.';

export function useSchedules() {
  const siteId = useSafeSiteId();
  return useQuery<TaskSchedule[]>({
    queryKey: schedulesKey(siteId),
    queryFn: () => fetchSchedules(siteId as SiteId),
    enabled: Boolean(siteId),
  });
}

/**
 * `GET /occurrences` returns an envelope so it can report that its own
 * expansion was capped. Older servers — and `tools/mock-rmf-server`,
 * which has not been updated — return a bare list.
 *
 * Both are accepted, and NOT out of politeness: a bare list is
 * indistinguishable from a complete one, so the only safe reading of it
 * is "complete, cap unknown". Coercing it into the envelope here, once,
 * is what keeps every consumer from having to know which server it is
 * talking to.
 */
function asOccurrencePage(body: unknown): ScheduleOccurrencePage {
  if (Array.isArray(body)) {
    return { occurrences: body as ScheduleOccurrence[], truncated: false, limit: body.length };
  }
  const page = body as Partial<ScheduleOccurrencePage> | null;
  return {
    occurrences: page?.occurrences ?? [],
    truncated: page?.truncated === true,
    limit: typeof page?.limit === 'number' ? page.limit : 0,
  };
}

/**
 * Occurrences inside `window`, or nothing at all when the caller has no
 * window yet (the calendar reports its visible range after its first
 * paint, so `null` is the normal state for exactly one render).
 */
export function useScheduleOccurrences(window: OccurrenceWindow | null) {
  const siteId = useSafeSiteId();
  const fromMs = window?.fromMs ?? 0;
  const toMs = window?.toMs ?? 0;
  return useQuery<ScheduleOccurrencePage>({
    queryKey: scheduleOccurrencesKey(siteId, fromMs, toMs),
    queryFn: async () =>
      // `fetchScheduleOccurrences` still declares `ScheduleOccurrence[]`
      // (`src/api/v1-client.ts`, an unchecked `as` over `res.json()`),
      // so its return type is a claim about the wire, not a check of
      // it. Normalised here until that declaration is corrected.
      asOccurrencePage(await fetchScheduleOccurrences(siteId as SiteId, fromMs, toMs)),
    enabled: Boolean(siteId) && window !== null,
    // An occurrence's `status` flips when the runner fires it, and the
    // runner ticks on its own clock, so a window covering "now" goes
    // stale without anything the SPA did.
    staleTime: 15_000,
  });
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<TaskSchedule, Error, CreateTaskScheduleRequest>({
    mutationFn: (body) => createSchedule(siteId as SiteId, body),
    onSuccess: () => invalidateSchedules(qc, siteId),
  });
}

export interface UpdateScheduleVars {
  scheduleId: string;
  /** ONLY the fields the operator changed. A timing change (`rrule`,
   *  `dtstart_ms`, `timezone` — see `timingChanged` in `rrule.ts`) goes
   *  with its `apply_from_ms`, which rides in the body as the server
   *  expects it; omitted, the server applies it from now. */
  patch: UpdateTaskScheduleRequest;
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<TaskSchedule, Error, UpdateScheduleVars>({
    mutationFn: ({ scheduleId, patch }) => updateSchedule(siteId as SiteId, scheduleId, patch),
    onSuccess: () => invalidateSchedules(qc, siteId),
  });
}

/** How long a dry-run answer is good for. It is computed against the
 *  server's NOW (the cut, the first run, "later today"), so it ages on
 *  its own; the occurrences query's figure, for the same reason. */
const PREVIEW_STALE_MS = 15_000;

/** Kept briefly: every debounced body is its own entry, and an editor
 *  session can produce dozens nobody will ask for again. */
const PREVIEW_GC_MS = 60_000;

/**
 * What saving `patch` over `scheduleId` WOULD do — the next runs, the
 * skips and moves it drops, a run later today it loses — for a live line
 * under the schedule editor.
 *
 * A QUERY rather than a mutation, because a live preview is a read that
 * is re-asked whenever its input changes, which is exactly what a query
 * key does: the body is in the key, so going back to an earlier value is
 * a cache hit, and a request a newer body has superseded is aborted
 * through its `signal` instead of racing it home. `placeholderData` keeps
 * the previous answer on screen while the next loads — for the SAME
 * schedule only, never another schedule's runs — so read
 * `isPlaceholderData` before presenting `data` as the answer to the body
 * CURRENTLY in the form.
 *
 * Debouncing is the caller's: pass `null` (or the last settled body)
 * while the operator is still typing, and the hook asks nothing.
 *
 * Off while "View as" is on — the preview is a POST, and the write gate
 * refuses every POST in a preview (with the banner's "actions are off"
 * flash) whatever the endpoint does with it. Read from the gate's own
 * state (`usePreviewRole`) so the two cannot disagree.
 *
 * Retries only what can heal: a 503 `retryable` validation timeout or a
 * network failure, once. A 400 is the server's answer about THIS body —
 * the same 400 the save would get — and asking again changes nothing.
 */
export function useScheduleUpdatePreview(
  scheduleId: string | null,
  patch: UpdateTaskScheduleRequest | null,
) {
  const siteId = useSafeSiteId();
  const previewRole = usePreviewRole();
  return useQuery<ScheduleUpdatePreview>({
    queryKey: schedulePreviewKey(siteId, scheduleId ?? '', patch ?? {}),
    queryFn: ({ signal }) =>
      previewScheduleUpdate(siteId as SiteId, scheduleId as string, patch ?? {}, signal),
    enabled: Boolean(siteId) && scheduleId !== null && patch !== null && previewRole === null,
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === scheduleId ? previous : undefined,
    staleTime: PREVIEW_STALE_MS,
    gcTime: PREVIEW_GC_MS,
    retry: (failureCount, error) =>
      failureCount < 1 && !(error instanceof ApiClientError && error.status < 500),
  });
}

export interface SetScheduleEnabledVars {
  scheduleId: string;
  enabled: boolean;
}

/**
 * Pause or resume a series without destroying it.
 *
 * `enabled` has been on the schedule row and in the PATCH contract from
 * the start, and the runner and the calendar both already honour it —
 * but nothing could ever set it to false: the create form hardcoded
 * `true` and DELETE was the only series verb the UI reached. A hotel
 * stopping the 06:00 clean for a fortnight of renovations therefore had
 * to delete the rule and rebuild it afterwards from memory, losing the
 * schedule's fire history with it (`delete_schedule` drops the
 * `schedule_fires` rows).
 *
 * Distinct from `useUpdateSchedule` on purpose: this is a verb an
 * operator performs, and naming it makes the call sites — and the
 * invalidation they need — obvious.
 */
export function useSetScheduleEnabled() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<TaskSchedule, Error, SetScheduleEnabledVars>({
    mutationFn: ({ scheduleId, enabled }) =>
      updateSchedule(siteId as SiteId, scheduleId, { enabled }),
    onSuccess: () => invalidateSchedules(qc, siteId),
  });
}

/**
 * Read the schedules, their runs and any dry run again -- after a batch
 * of writes to the rows of one plan in which one failed (`plan-batch`),
 * so the screen draws each row as it now is, not as it was before the
 * batch. A write that succeeds already does this for itself.
 */
export function useRefreshSchedules(): () => Promise<unknown> {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useCallback(() => invalidateSchedules(qc, siteId), [qc, siteId]);
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<void, Error, string>({
    mutationFn: (scheduleId) => deleteSchedule(siteId as SiteId, scheduleId),
    onSuccess: () => invalidateSchedules(qc, siteId),
  });
}

export interface OccurrenceRef {
  scheduleId: string;
  /** What the RULE produced, not where the occurrence currently sits —
   *  the server keys exdates and overrides on that, so a moved
   *  occurrence keeps its identity. */
  originalStartMs: number;
}

/** "Delete this event" — drops one occurrence, leaves the series. */
export function useSkipOccurrence() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<TaskSchedule, Error, OccurrenceRef>({
    mutationFn: ({ scheduleId, originalStartMs }) =>
      addScheduleExdate(siteId as SiteId, scheduleId, originalStartMs),
    onSuccess: () => invalidateSchedules(qc, siteId),
  });
}

export interface OverrideOccurrenceVars extends OccurrenceRef {
  override: ScheduleOccurrenceOverride;
}

/** "Edit this event only" — moves or re-payloads one occurrence. */
export function useOverrideOccurrence() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<void, Error, OverrideOccurrenceVars>({
    mutationFn: ({ scheduleId, originalStartMs, override }) =>
      putScheduleOverride(siteId as SiteId, scheduleId, originalStartMs, override),
    onSuccess: () => invalidateSchedules(qc, siteId),
  });
}

/**
 * "Start now" — dispatch one scheduled occurrence early (the phone
 * calendar). The occurrence then reads as fired, and the task it became
 * arrives on the live list, so both the occurrences and the tasks are
 * re-read.
 */
export function useRunOccurrenceNow() {
  const qc = useQueryClient();
  const siteId = useSafeSiteId();
  return useMutation<RunOccurrenceResponse, Error, OccurrenceRef>({
    mutationFn: ({ scheduleId, originalStartMs }) =>
      runScheduleOccurrence(siteId as SiteId, scheduleId, originalStartMs),
    onSuccess: () =>
      Promise.all([
        invalidateSchedules(qc, siteId),
        qc.invalidateQueries({
          queryKey: tasksQueryOptions(siteId ? (siteId as SiteId) : null).queryKey,
        }),
      ]),
  });
}

function invalidateSchedules(
  qc: ReturnType<typeof useQueryClient>,
  siteId: string,
): Promise<unknown> {
  return Promise.all([
    qc.invalidateQueries({ queryKey: schedulesKey(siteId) }),
    qc.invalidateQueries({ queryKey: [SCHEDULE_OCCURRENCES_KEY, siteId] }),
    // A dry run is an answer about the schedule as it WAS: a skip, a
    // pause or another save since changes the runs it listed.
    qc.invalidateQueries({ queryKey: [SCHEDULE_PREVIEW_KEY, siteId] }),
    // The Month view's plan check walks these runs (2026-10-31).
    invalidatePlanCheck(qc, siteId),
  ]);
}
